import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useUIContext } from "../../contexts/UIContext"; // Import UIContext
import { useDebounce } from "../../hooks/useDebounce";
import Modal from "./Modal";
import ManageAddresses from "../customer/ManageAddresses";
import HeaderInput from "../utilComponents/HeaderInput";
import Button from "../utilComponents/Button";
import LocationDropdown from "../utilComponents/LocationDropdown";
import logo from "../../assets/images/logo.png";
import { useLogoutMutation } from "../../slices/authSlice";
import {
  useFetchWarehouseStatesQuery,
  useSearchProductsAndStoresQuery,
} from "../../slices/headerApiSlice";
import { logout as logoutAction } from "../../slices/customerSlice";

const Header = () => {
  const dropdownRef = useRef(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);
  const [showDropdown, setShowDropdown] = useState(false);

  const { data, isLoading: isLoadingProductandStores } =
    useSearchProductsAndStoresQuery(debouncedQuery, {
      skip: !debouncedQuery,
    });

  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const profileRef = useRef(null);

  const { toggleSidebar, openModal, closeModal } = useUIContext(); // Access context state management
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [logout, { isLoading }] = useLogoutMutation();

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const { data: warehouseStates = [], isLoading: isWarehouseLoading } =
    useFetchWarehouseStatesQuery();

  const selectedLocation = localStorage.getItem("location");

  const { info: customer } = useSelector((state) => state.frontendAuth);
  const cartItemCount = useSelector((state) => state.cart.itemCount);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false); // Close the dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!selectedLocation) {
      if (warehouseStates.length === 1) {
        // Automatically set location if there's only one option
        handleLocationChange(warehouseStates[0]);
      } else {
        // Show the modal if there are multiple options
        setIsLocationModalOpen(true);
      }
    }
  }, [selectedLocation, warehouseStates]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileRef]);

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      dispatch(logoutAction());
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleLocationChange = (newLocation) => {
    localStorage.setItem("location", newLocation);
    setIsLocationModalOpen(false);
    window.location.reload(); // Reload the page
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    setShowDropdown(true);
  };

  // const handleResultClick = (type, id) => {
  //   if (type === "product") navigate(`/products/${id}`);
  //   if (type === "store") navigate(`/stores/${id}`);
  // };

  const handleResultClick = (type, idOrName) => {
    if (type === "product") navigate(`/products/${idOrName}`);
    if (type === "store") navigate(`/vendors/${idOrName}`); // Changed to use vendor route
  };

  const handleSearchSubmit = () => {
    if (query.trim()) {
      setShowDropdown(false);
      navigate(`/search?query=${encodeURIComponent(query.trim())}`);
    } else {
      setShowDropdown(false);
      navigate(`/search`);
    }
  };

  const dropdownItems = [
    { label: "Orders", icon: "shopping_bag", path: "/orders" },
    { label: "Saved Items", icon: "favorite", path: "/saved-items" },
    {
      label: "Manage Account",
      icon: "manage_accounts",
      path: "/manage-account",
    },
    {
      label: "Become a Supplier",
      icon: "local_shipping",
      path: "/become-supplier",
    },
    { label: "Support", icon: "headset_mic", path: "/support" },

    {
      label: "Manage Address",
      icon: "location_on",
      action: () => openModal(<ManageAddresses onClose={closeModal} />),
    },
    { label: "Logout", icon: "logout", isLogout: true },
  ];

  return (
    <header>
      {/* Desktop Header */}
      {/* <div className="hidden lg:flex justify-between mx-24 py-3"> */}
      <div className="hidden lg:flex justify-between px-24 py-3 fixed top-0 left-0 w-full z-50 bg-white shadow">
        <div className="flex items-center space-x-12">
          <Link to="/">
            <img src={logo} alt="logo" className="w-36 cursor-pointer" />
          </Link>
          <a
            href="https://vendor.agrolinka.com/signup"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-4 text-sm flex items-center"
          >
            <span className="material-symbols-rounded font-thin text-3xl text-agBlack">
              storefront
            </span>
            <span className="ml-1 text-agBlack hover:text-agBlack">
              Become a Vendor
            </span>
          </a>
        </div>
        <div className="flex flex-row items-center space-x-2 relative">
          {!isWarehouseLoading && (
            <LocationDropdown
              options={warehouseStates}
              value={selectedLocation}
              onChange={handleLocationChange}
            />
          )}
          <div className="w-[24vw] relative " ref={dropdownRef}>
            <HeaderInput
              placeholder="Search anything"
              value={query}
              onChange={handleInputChange}
            />
            {/* Dropdown for search results */}
            {showDropdown && query && (
              <div className="absolute top-full left-0 w-full bg-white shadow-lg rounded-lg mt-1 z-10">
                {isLoadingProductandStores ? (
                  <p className="p-2 text-sm text-gray-500">Searching...</p>
                ) : (
                  <>
                    {/* Render products */}
                    {data?.products?.length > 0 && (
                      <div className="p-2">
                        <p className="text-sm font-semibold text-agBlack font-nunito ml-2 border-b">
                          Products
                        </p>
                        {data.products.map((product) => (
                          <div
                            key={product._id}
                            className="text-sm text-agLightGray rounded-md py-1 px-2 my-1 hover:bg-agBg cursor-pointer"
                            onMouseDown={() => {
                              handleResultClick("product", product._id);
                              setShowDropdown(false); // Close dropdown
                            }}
                          >
                            {product.title}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Render stores */}
                    {data?.stores?.length > 0 && (
                      <div className="p-2">
                        <p className="text-sm font-semibold text-agBlack font-nunito ml-2 border-b">
                          Stores
                        </p>
                        {data.stores.map((store) => (
                          <div
                            key={store._id}
                            className="text-sm text-agLightGray rounded-md py-1 px-2 my-1 hover:bg-agBg cursor-pointer"
                            onMouseDown={() => {
                              // handleResultClick("store", store._id);
                              handleResultClick("store", store.storeName);
                              setShowDropdown(false); // Close dropdown
                            }}
                          >
                            {store.storeName}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* No results */}
                    {data?.products?.length === 0 &&
                      data?.stores?.length === 0 && (
                        <p className="p-2 text-sm text-gray-500">
                          No results found
                        </p>
                      )}
                  </>
                )}
              </div>
            )}
          </div>

          <Button onMouseDown={handleSearchSubmit}>SEARCH</Button>
        </div>
        <div className="flex items-center space-x-4">
          {!customer ? (
            <Link to="/login">
              <p className="agBlack text-sm">Login/ Signup</p>
            </Link>
          ) : (
            <div className="relative" ref={profileRef}>
              <div
                className="ml-4 text-sm flex items-center cursor-pointer"
                onMouseDown={toggleProfileDropdown}
              >
                <span className="material-symbols-outlined font-thin text-3xl text-agBlack">
                  person
                </span>
                <span className="ml-1 text-agBlack">
                  Hi, {customer.firstName}
                </span>
                <span className="material-symbols-rounded 2xl">
                  arrow_drop_down
                </span>
              </div>

              {isProfileDropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg p-2 z-10 card">
                  {dropdownItems.map((item, index) => (
                    <div
                      key={index}
                      className={`flex items-center py-2 px-3 text-sm cursor-pointer ${
                        item.isLogout
                          ? "text-red-600 hover:bg-red-100 rounded-lg"
                          : "text-agBlack hover:bg-agPrimaryLight rounded-lg"
                      }`}
                      onMouseDown={
                        item.isLogout
                          ? handleLogout
                          : item.action
                            ? item.action
                            : () => navigate(item.path)
                      }
                    >
                      <span className="material-symbols-rounded font-thin mr-2">
                        {item.icon}
                      </span>
                      {item.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <Link to="/cart" className="ml-4 text-sm flex items-center relative">
            <span className="material-symbols-rounded font-thin text-3xl text-agBlack">
              shopping_cart
            </span>
            {cartItemCount > 0 && (
              <div className="absolute -top-2 -right-2 bg-red-500 border-2 p-1 border-white text-white rounded-full text-xs w-6 h-6 flex items-center justify-center">
                {cartItemCount}
              </div>
            )}
          </Link>
        </div>
      </div>

      {/* Mobile Header */}
      {/* <div className="lg:hidden flex flex-col space-y-0 py-2"> */}
      <div className="lg:hidden flex flex-col space-y-0 py-2 fixed top-0 left-0 w-full z-50 bg-white shadow">
        <div className="flex items-center justify-between mx-2 mb-2">
          <div className="flex items-center space-x-2">
            <span
              className="material-symbols-rounded font-thin text-3xl cursor-pointer"
              onMouseDown={toggleSidebar} // Use context toggleSidebar
            >
              menu
            </span>
            <Link to="/">
              <img src={logo} alt="logo" className="w-28" />
            </Link>
          </div>
          <div className="flex items-center space-x-2">
            {!isWarehouseLoading && (
              <LocationDropdown
                options={warehouseStates}
                value={selectedLocation}
                onChange={handleLocationChange}
              />
            )}
            <Link
              to="/cart"
              className="ml-4 text-sm flex items-center relative"
            >
              <span className="material-symbols-rounded font-thin text-3xl text-agBlack">
                shopping_cart
              </span>
              {cartItemCount > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 border-2 p-1 border-white text-white rounded-full text-xs w-6 h-6 flex items-center justify-center">
                  {cartItemCount}
                </div>
              )}
            </Link>
          </div>
        </div>

        <div className="flex space-x-2 mx-2 mb-2">
          {/* <div
            className="flex-grow"
            onMouseDown={() => setShowDropdown(true)} // Open dropdown on click
          > */}
          <div className="w-full">
            <HeaderInput
              placeholder="Search anything"
              value={query}
              onChange={handleInputChange}
            />
          </div>
          {/* </div> */}
          <Button>SEARCH</Button>
        </div>

        {/* Full-Screen Dropdown */}
        {showDropdown && (
          <div className="fixed inset-0 bg-white z-50 flex flex-col p-4">
            {/* Back Button and Search Bar */}
            <div className="flex items-center space-x-2 mb-4">
              <span
                className="material-symbols-rounded text-2xl cursor-pointer"
                onMouseDown={() => setShowDropdown(false)} // Close dropdown
              >
                arrow_back
              </span>
              <HeaderInput
                placeholder="Search anything"
                value={query}
                onChange={handleInputChange}
                isFullWidth
              />
              <Button onMouseDown={handleSearchSubmit}>SEARCH</Button>
            </div>

            {/* Search Results */}
            {/* <div className="relative flex flex-col overflow-y-auto"> */}
            <div className="flex-1 overflow-y-auto">
              {isLoadingProductandStores ? (
                <p className="p-2 text-sm text-gray-500">Searching...</p>
              ) : (
                <>
                  {/* Render Products */}
                  {data?.products?.length > 0 && (
                    <div className="p-2">
                      <p className="text-sm font-semibold text-agBlack font-nunito ml-2 border-b">
                        Products
                      </p>
                      {data.products.map((product) => (
                        <div
                          key={product._id}
                          className="text-sm text-agLightGray rounded-md py-1 px-2 my-1 hover:bg-agBg cursor-pointer"
                          // onMouseDown={() => {
                          //   handleResultClick("product", product._id);
                          //   setShowDropdown(false); // Close dropdown
                          // }}
                          onMouseDown={() => {
                            setShowDropdown(false); // Hide the dropdown first
                            // Delay navigation AFTER dropdown has been visually removed
                            requestAnimationFrame(() => {
                              setTimeout(
                                () => handleResultClick("product", product._id),
                                50,
                              );
                            });
                          }}
                        >
                          {product.title}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Render Stores */}
                  {data?.stores?.length > 0 && (
                    <div className="p-2">
                      <p className="text-sm font-semibold text-agBlack font-nunito ml-2 border-b">
                        Stores
                      </p>
                      {data.stores.map((store) => (
                        <div
                          key={store._id}
                          className="text-sm text-agLightGray rounded-md py-1 px-2 my-1 hover:bg-agBg cursor-pointer"
                          // onMouseDown={() => {
                          //   handleResultClick("store", store._id);
                          //   setShowDropdown(false); // Close dropdown
                          // }}
                          onMouseDown={() => {
                            setShowDropdown(false);
                            requestAnimationFrame(() => {
                              setTimeout(
                                () => handleResultClick("store", store._id),
                                50,
                              );
                            });
                          }}
                        >
                          {store.storeName}
                        </div>
                      ))}
                    </div>
                  )}
                  {/* No Results */}
                  {data?.products?.length === 0 &&
                    data?.stores?.length === 0 && (
                      <p className="p-2 text-sm text-gray-500">
                        No results found
                      </p>
                    )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Location Selection Modal */}
      <Modal isVisible={isLocationModalOpen} onClose={() => {}}>
        <div className="text-center px-6 py-8">
          <h2 className="text-lg md:text-xl font-semibold text-agBlack">
            Welcome to Agrolinka
          </h2>
          <p className="mt-4 text-sm text-agLightGray">
            Where are you shopping from?
          </p>
          <div className="mt-6 space-y-4 text-sm">
            {warehouseStates.map((state, index) => (
              <div key={index} className="flex items-center space-x-3">
                <input
                  type="radio"
                  id={`state-${state}`}
                  name="location"
                  value={state}
                  className="w-3 h-3 accent-agLightGray text-sm"
                  onChange={(e) => {
                    handleLocationChange(e.target.value);
                  }}
                  checked={selectedLocation === state}
                />
                <label
                  htmlFor={`state-${state}`}
                  className="text-sm text-agLightGray"
                >
                  {state}
                </label>
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </header>
  );
};

export default Header;
